import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { api, useAuthStore } from '@claimshield/shared'
import { fetchStateAccess, progress as postProgress, upload as postUpload, type StateAccessRow } from '@/services/stateAccess'

const waitingStatuses = new Set(['not_started', 'packet_sent', 'client_submitted_to_state'])
export function waitingOnClient(status: string): boolean { return waitingStatuses.has(status) }
export function deadlineDays(date: string, now = new Date()): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date)
  if (!match) return 0
  const target = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())
  return Math.round((target - today) / 86400000)
}
export function deadlineLabel(date: string, now = new Date()): string {
  const days = deadlineDays(date, now)
  return days >= 0 ? `in ${days} days` : `${-days} days ago`
}
function safeError(error: any, code?: string): string {
  const detail = error?.response?.data?.detail
  const message = typeof detail === 'string' ? detail : 'We could not save your update. Please try again.'
  return code ? message.split(code).join('[redacted]') : message
}

export const useStateAccessStore = defineStore('stateAccess', () => {
  const auth = useAuthStore()
  const enabled = ref(false)
  const checked = ref(false)
  const rows = ref<StateAccessRow[]>([])
  const loading = ref(false)
  const error = ref('')
  const actionError = ref('')
  const busyState = ref('')
  const visibleRows = computed(() => rows.value.filter(row => row.status !== 'not_required'))
  const waiting = computed(() => visibleRows.value.some(row => waitingOnClient(row.status)))
  let identity: string | null = null
  let request = 0
  let source: 'portal' | 'token' = 'portal'
  let onboardingToken: string | null = null
  function currentIdentity(): string | null { return auth.viewAsClientId ?? auth.user?.id ?? null }
  async function ensureFeature(): Promise<void> {
    if (source !== 'portal') {
      source = 'portal'
      onboardingToken = null
      checked.value = false
      enabled.value = false
      rows.value = []
      busyState.value = ''
      actionError.value = ''
    }
    const key = currentIdentity()
    if (checked.value && key === identity) return
    identity = key
    enabled.value = false
    checked.value = false
    rows.value = []
    const id = ++request
    try {
      const response = await api.get<{ features?: Record<string, boolean>; data?: { features?: Record<string, boolean> } }>('/v1/client/features')
      if (id !== request || currentIdentity() !== key) return
      enabled.value = (response.data.features ?? response.data.data?.features)?.['state-access-portal'] === true
    } catch {
      if (id !== request) return
      enabled.value = false
    } finally {
      if (id === request) checked.value = true
    }
  }
  async function fetch(): Promise<void> {
    await ensureFeature()
    if (source !== 'portal' || !enabled.value) return
    const id = ++request
    loading.value = true
    error.value = ''
    try {
      const response = await fetchStateAccess()
      if (id === request) rows.value = response.data.rows ?? []
    } catch (err: any) {
      if (id !== request) return
      if (err?.response?.status === 404) rows.value = []
      else error.value = 'We could not load state access. Please try again.'
    } finally { if (id === request) loading.value = false }
  }
  async function fetchToken(token: string): Promise<void> {
    if (source === 'token' && onboardingToken === token && (checked.value || loading.value)) return
    source = 'token'
    onboardingToken = token
    checked.value = false
    enabled.value = false
    rows.value = []
    error.value = ''
    actionError.value = ''
    busyState.value = ''
    loading.value = true
    const id = ++request
    try {
      const response = await fetchStateAccess(token)
      if (id !== request || source !== 'token' || onboardingToken !== token) return
      rows.value = response.data.rows ?? []
      enabled.value = true
    } catch (err: any) {
      if (id !== request || source !== 'token' || onboardingToken !== token) return
      if (err?.response?.status === 404) {
        rows.value = []
        enabled.value = false
      } else {
        enabled.value = true
        error.value = 'We could not load state access. Please try again.'
      }
    } finally {
      if (id === request && source === 'token' && onboardingToken === token) {
        checked.value = true
        loading.value = false
      }
    }
  }
  function updateRow(row: StateAccessRow): void {
    const index = rows.value.findIndex(existing => existing.state === row.state)
    if (index >= 0) rows.value[index] = { ...rows.value[index], ...row }
  }
  async function progress(state: string, options: { reference?: string; code?: string } = {}): Promise<boolean> {
    if (!enabled.value) return false
    const actionSource = source
    const actionToken = onboardingToken
    const actionIdentity = currentIdentity()
    const sameScope = () => source === actionSource && onboardingToken === actionToken
      && (actionSource === 'token' || currentIdentity() === actionIdentity)
    actionError.value = ''
    busyState.value = state
    try {
      const response = await postProgress(state, options, source === 'token' ? onboardingToken ?? undefined : undefined)
      if (!sameScope()) return true
      if (response.data?.state) updateRow(response.data)
      else if (source === 'portal') await fetch()
      return true
    } catch (err) {
      if (sameScope()) actionError.value = safeError(err, options.code)
      return false
    } finally { if (sameScope()) busyState.value = '' }
  }
  async function upload(state: string, file: File): Promise<boolean> {
    if (!enabled.value) return false
    const actionSource = source
    const actionToken = onboardingToken
    const actionIdentity = currentIdentity()
    const sameScope = () => source === actionSource && onboardingToken === actionToken
      && (actionSource === 'token' || currentIdentity() === actionIdentity)
    actionError.value = ''
    busyState.value = state
    try {
      const response = await postUpload(state, file, source === 'token' ? onboardingToken ?? undefined : undefined)
      if (!sameScope()) return true
      if (response.data?.state) updateRow(response.data)
      else if (source === 'portal') await fetch()
      return true
    } catch (err) {
      if (sameScope()) actionError.value = safeError(err)
      return false
    } finally { if (sameScope()) busyState.value = '' }
  }
  return { enabled, checked, rows, loading, error, actionError, busyState, visibleRows, waiting, ensureFeature, fetch, fetchToken, progress, upload }
})
